
import { Logger, SingleIndex } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component } from '../../../concepts/objects';
import { ObjectManager } from '../../../concepts/objects/ObjectManager';
import { ActiveObject, resolve } from '../../Container';
import { Comment } from './Comment';
import { Commentable } from './Commentable';
import { CommentCountAttr } from './CommentAssociations';

const metrics = Logger.get('app.metrics');

export class ApplyCommentCountMechanics extends ActiveObject {

    @resolve(CommandDispatcher) dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects!: ObjectManager;

    private readonly commentLookup = new SingleIndex<string, Comment>();
    private readonly subjectLookup = new Map<Comment, string>();
    private readonly invalidations = new Set<Comment>();

    public start(): void {
        this.rebuild();
        this.burden(
            this.objects.on('Comment', 'allocated', this.onCommentChanged),
            this.objects.on('Comment', 'affected', this.onCommentChanged),
            this.objects.on('Comment', 'released', this.onCommentChanged),
            this.objects.on('updated', () => this.refresh()),
        );
    }

    private rebuild() {
        const t = performance.now();

        const results = this.objects.getAll(Comment);
        results.forEach(x => void (this.track(x)));
        this.applyCounts(new Set(this.subjectLookup.values()));

        metrics.log('ApplyCommentCountMechanics.rebuild', `${performance.now() - t}ms`);
    }

    private refresh() {
        const recalc = new Set<string>();
        for (const comm of this.invalidations) {
            if (this.track(comm)) {
                recalc.add(this.subjectLookup.get(comm));
            }
            else {
                recalc.add(this.subjectLookup.get(comm));
                this.untrack(comm);
            }
        }
        this.invalidations.clear();
        this.applyCounts(recalc);
    }

    private track(comm: Comment) {
        if (!comm.alive || !comm.parent) {
            return false;
        }
        this.commentLookup.add(comm.parent.id, comm);
        this.subjectLookup.set(comm, comm.parent.id);
        return true;
    }

    private untrack(comm: Comment) {
        const subjectId = this.subjectLookup.get(comm);
        if (subjectId) {
            this.commentLookup.remove(subjectId, comm);
            this.subjectLookup.delete(comm);
            return true;
        }
        return false;
    }

    private applyCounts(subjectIds: Set<string>) {
        for (const id of subjectIds) {
            const sub = this.objects.getMaybe(id, Commentable);
            if (!sub) continue;
            const count = this.commentLookup.get(id).size;
            CommentCountAttr.set(sub, count);
        }
    }

    private onCommentChanged = ([cmpt]: [Component]) => {
        this.invalidations.add(cmpt.cast(Comment));
    };
}
